// Spec §9.5 / Phase B4 — ConsolidationRule create/edit modal. Closes the
// "missing JSON conditions/limits editor" gap: bucket-key components are
// chosen via MultiSelect, all numeric limits surface as NumberInputs,
// boolean toggles use Switch. No hand-written JSON.

import {
  Checkbox,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import type {
  BucketKeyComponent,
  ICreateConsolidationRuleInput,
  IConsolidationRule,
} from "../../apis/consolidation-rule"
import { useSettingsMutations } from "./hooks"
import { SettingsModal } from "./settings-modal"

const BUCKET_COMPONENTS: BucketKeyComponent[] = [
  "SENDER_BUSINESS",
  "RECEIVER_BUSINESS",
  "DELIVERY_DATE",
  "SHIP_TO_ADDRESS",
  "DELIVERY_CHANNEL",
  "DELIVERY_ZONE",
]

type FormShape = {
  name: string
  description: string
  bucketKeyComponents: BucketKeyComponent[]
  maxOrdersPerBucket: number
  maxDnPerRun: number
  sameDayOnly: boolean
  matchAddressStrict: boolean
  autoRunEnabled: boolean
  isActive: boolean
  priority: number
}

type Props = {
  opened: boolean
  onClose: () => void
  initial?: IConsolidationRule | null
}

function fromInitial (r?: IConsolidationRule | null): FormShape {
  return {
    name               : r?.name ?? "",
    description        : r?.description ?? "",
    bucketKeyComponents: (r?.bucketKeyComponents as BucketKeyComponent[]) ?? ["SENDER_BUSINESS", "RECEIVER_BUSINESS", "DELIVERY_DATE", "SHIP_TO_ADDRESS"],
    maxOrdersPerBucket : r?.maxOrdersPerBucket ?? 50,
    maxDnPerRun        : r?.maxDnPerRun ?? 200,
    sameDayOnly        : r?.sameDayOnly ?? true,
    matchAddressStrict : r?.matchAddressStrict ?? true,
    autoRunEnabled     : r?.autoRunEnabled ?? false,
    isActive           : r?.isActive ?? true,
    priority           : r?.priority ?? 0,
  }
}

export function ConsolidationRuleFormModal ({ opened, onClose, initial }: Props) {
  const { t } = useTranslation("settings")
  const mutations = useSettingsMutations()
  const isEdit = !!initial

  // BE enum (CONSOLIDATION_BUCKET_COMPONENT); the engine hashes the chosen parts in this order.
  const bucketOptions: { value: BucketKeyComponent; label: string }[] = BUCKET_COMPONENTS.map((value) => ({
    value,
    label: t(`consolidation.options.bucketKey.${value}`),
  }))

  const form = useForm<FormShape>({
    initialValues: fromInitial(initial),
    validate     : {
      name               : (v) => (!v.trim() ? t("consolidation.fields.name.label") + " " + t("common.empty").toLowerCase() : null),
      bucketKeyComponents: (v) => (v.length === 0 ? t("consolidation.fields.bucketKeyComponents.label") : null),
    },
  })

  // Reset when initial changes (modal reuses one instance for both create/edit).
  // We rely on key prop from caller for clean state when initial flips.

  const handleSubmit = async (values: FormShape) => {
    const payload: ICreateConsolidationRuleInput = {
      name               : values.name.trim(),
      description        : values.description.trim() || null,
      bucketKeyComponents: values.bucketKeyComponents,
      maxOrdersPerBucket : values.maxOrdersPerBucket,
      maxDnPerRun        : values.maxDnPerRun,
      sameDayOnly        : values.sameDayOnly,
      matchAddressStrict : values.matchAddressStrict,
      autoRunEnabled     : values.autoRunEnabled,
      isActive           : values.isActive,
      priority           : values.priority,
    }
    if (isEdit && initial) {
      await mutations.consolidationRule.update(initial.id, payload)
    } else {
      await mutations.consolidationRule.create(payload)
    }
    onClose()
    form.reset()
  }

  return (
    <SettingsModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("consolidation.modal.editTitle") : t("consolidation.modal.createTitle")}
      onSubmit={form.onSubmit(handleSubmit)}
      submitLabel={isEdit ? t("common.save") : t("common.create")}
      submitting={mutations.submitting}
    >

          {/* Card 1: Бүлгэх дүрэм */}
          <Card withBorder radius="md" p="md">
            <Text fw={600} size="sm" mb="sm">{t("consolidation.cards.bucketingRule.title")}</Text>
            <Stack gap="sm">
              <TextInput
                label={t("consolidation.fields.name.label")}
                description={t("consolidation.fields.name.description")}
                required
                {...form.getInputProps("name")}
              />
              <Textarea
                label={t("consolidation.fields.description.label")}
                description={t("consolidation.fields.description.description")}
                autosize
                minRows={2}
                {...form.getInputProps("description")}
              />
              <MultiSelect
                label={<>{t("consolidation.fields.bucketKeyComponents.label")}<HelpIcon helpKey="order.settings.field.bucket-key" /></>}
                description={t("consolidation.fields.bucketKeyComponents.description")}
                data={bucketOptions}
                required
                {...form.getInputProps("bucketKeyComponents")}
              />
              <Checkbox
                label={t("consolidation.fields.sameDayOnly.label")}
                description={t("consolidation.fields.sameDayOnly.description")}
                checked={form.values.sameDayOnly}
                onChange={(e) => form.setFieldValue("sameDayOnly", e.currentTarget.checked)}
              />
            </Stack>
          </Card>

          {/* Card 2: Хязгаар & дараалал */}
          <Card withBorder radius="md" p="md">
            <Text fw={600} size="sm" mb="sm">{t("consolidation.cards.limitsOrder.title")}</Text>
            <Stack gap="sm">
              <Group grow>
                <NumberInput
                  label={t("consolidation.fields.maxOrdersPerBucket.label")}
                  description={t("consolidation.fields.maxOrdersPerBucket.description")}
                  min={1}
                  {...form.getInputProps("maxOrdersPerBucket")}
                />
                <NumberInput
                  label={t("consolidation.fields.maxRunCreatesDn.label")}
                  description={t("consolidation.fields.maxRunCreatesDn.description")}
                  min={1}
                  {...form.getInputProps("maxDnPerRun")}
                />
              </Group>
              <NumberInput
                label={t("consolidation.fields.priority.label")}
                description={t("consolidation.fields.priority.description")}
                min={0}
                {...form.getInputProps("priority")}
              />
              <Checkbox
                label={<>{t("consolidation.fields.autoRunEnabled.label")}<HelpIcon helpKey="order.settings.field.auto-run" /></>}
                description={t("consolidation.fields.autoRunEnabled.description")}
                checked={form.values.autoRunEnabled}
                onChange={(e) => form.setFieldValue("autoRunEnabled", e.currentTarget.checked)}
              />
              <Checkbox
                label={t("common.active")}
                checked={form.values.isActive}
                onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
              />
            </Stack>
          </Card>
    </SettingsModal>
  )
}
